import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackChallenge } from "@/lib/ga4";
import { VimeoEmbed } from "@/components/challenge/VimeoEmbed";

const Page = ({ children, view }) => (
  <div className="min-h-screen bg-[#FFF9F2] px-4 py-12" data-testid="challenge-wrapup-page" data-view-state={view}>
    <div className="max-w-3xl mx-auto">{children}</div>
  </div>
);

export default function ChallengeWrapup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const loading = user === null;
  const [st, setSt] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?next=/challenge/wrapup"); return; }
    api.get("/challenge2/wrapup")
      .then(({ data }) => { setSt({ loading: false, data }); trackChallenge("wrapup_opened", { completed: data.challenge_completed }); })
      .catch((e) => setSt({ loading: false, error: e?.response?.status === 423 ? "locked" : "not_member" }));
  }, [loading, user, navigate]);

  const goCheckout = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/challenge2/checkout");
      trackChallenge("checkout_opened");
      window.location.href = data.checkout_url;
    } catch (_e) {
      setError("Der Checkout konnte nicht geöffnet werden. Bitte versuche es gleich noch einmal.");
      setBusy(false);
    }
  };

  if (st.loading) return <Page view="loading"><Loader2 className="w-8 h-8 animate-spin text-[#F3651D] mx-auto" /></Page>;
  if (st.error) {
    return (
      <Page view={st.error}>
        <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="challenge-wrapup-locked">
          <Lock className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="font-bold">{st.error === "locked" ? "Das Wrap-up öffnet sich mit Tag 5." : "Dieser Bereich ist für Challenge-Teilnehmer."}</p>
          <button type="button" onClick={() => navigate("/dashboard")} className="mt-5 text-sm text-slate-600 underline" data-testid="challenge-wrapup-back">Zum Mitgliederbereich</button>
        </div>
      </Page>
    );
  }

  const w = st.data.wrapup || {};
  return (
    <Page view="active">
      <button type="button" onClick={() => navigate("/challenge/tag/5")} className="text-xs text-slate-500 inline-flex items-center gap-1 mb-6" data-testid="challenge-wrapup-breadcrumb">
        <ArrowLeft className="w-3 h-3" /> Tag 5
      </button>
      <div className="rounded-2xl bg-white border-2 border-[#F3651D]/30 shadow-xl p-6 sm:p-8 space-y-5">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="challenge-wrapup-title">{w.title}</h1>
        <VimeoEmbed url={w.video_url} title={w.title} testId="challenge-wrapup-video" />
        {w.text && <p className="text-base text-slate-700 leading-relaxed whitespace-pre-line" data-testid="challenge-wrapup-text">{w.text}</p>}
        {!st.data.challenge_completed && (
          <p className="text-sm text-slate-500" data-testid="challenge-wrapup-incomplete">Du hast noch nicht alle Tage ausgefüllt. Deine offenen Tage bleiben jederzeit zugänglich.</p>
        )}
        {st.data.show_checkout ? (
          <div className="pt-2" data-testid="challenge-wrapup-checkout-block">
            <button type="button" onClick={goCheckout} disabled={busy} data-testid="challenge-wrapup-checkout-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-50 text-white font-extrabold px-6 py-4 transition-colors">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{w.cta_label} <ArrowRight className="w-5 h-5" /></>}
            </button>
            {w.note && <p className="mt-2 text-xs text-slate-500 text-center" data-testid="challenge-wrapup-checkout-note">{w.note}</p>}
            {error && <p className="mt-2 text-sm text-red-600" data-testid="challenge-wrapup-checkout-error">{error}</p>}
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="challenge-wrapup-no-checkout">Du hast bereits Zugang zu Pro.</p>
        )}
      </div>
    </Page>
  );
}
